import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { resolveEventAttendanceFlags } from '@/utils/admin/eventsAttendance';
import { AttendeeListItemProps } from './types';

export const AttendeeListItem: React.FC<AttendeeListItemProps> = ({ 
  resident, 
  onPress,
  eventId,
}) => {
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Prefer EventResident for current event; fallback to events[0].rsvpList if present
  const { checkedIn: isCheckedIn, hasRsvp } = resolveEventAttendanceFlags(resident, eventId);

  const accessibilityLabel = `${resident.firstName} ${resident.lastName}, ${resident.email}${resident.unitNumberSpace ? `, Unit ${resident.unitNumberSpace}` : ''}${hasRsvp ? ', RSVP confirmed' : ', No RSVP'}${isCheckedIn ? ', Checked in' : ', Not checked in'}`;

  return (
    <TouchableOpacity 
      style={styles.attendeeListItem} 
      onPress={onPress} 
      activeOpacity={0.7}
      accessibilityLabel={accessibilityLabel}
      accessibilityHint="Tap to view resident details and check-in options"
      accessibilityRole="button"
    >
      <View style={styles.avatarContainer}>
        <Text style={styles.avatarText}>
          {getInitials(resident.firstName, resident.lastName)}
        </Text>
        {isCheckedIn && (
          <View style={styles.checkedInBadge}>
            <Text style={styles.checkedInBadgeText}>✓</Text>
          </View>
        )}
      </View>
      <View style={styles.attendeeListItemContent}>
        <Text style={styles.attendeeListItemName}>
          {resident.firstName} {resident.lastName}
        </Text>
        <Text style={styles.attendeeListItemEmail}>{resident.email}</Text>
        <View style={styles.attendeeListItemFooter}>
          {resident.unitNumberSpace && (
            <View style={styles.unitBadge}>
              <Text style={styles.unitBadgeText}>Unit {resident.unitNumberSpace}</Text>
            </View>
          )}
          <View style={styles.statusContainer}>
            {hasRsvp && (
              <View style={styles.rsvpBadge}>
                <Text style={styles.rsvpBadgeText}>RSVP'd</Text>
              </View>
            )}
          </View>
        </View>
      </View>
      <View style={styles.checkInStatus}>
        {isCheckedIn ? (
          <View style={styles.checkedInStatus}>
            <View style={styles.checkedInDot} />
            <Text style={styles.checkedInText}>Checked In</Text>
          </View>
        ) : (
          <View style={styles.notCheckedInStatus}>
            <View style={styles.notCheckedInDot} />
            <Text style={styles.notCheckedInText}>Not Checked In</Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  attendeeListItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 16,
    marginBottom: 8,
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER + '30',
  },
  avatarContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: ADMIN_COLORS.PRIMARY + '20',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    position: 'relative',
  },
  avatarText: {
    fontSize: 16,
    fontWeight: '700',
    color: ADMIN_COLORS.PRIMARY,
  },
  checkedInBadge: {
    position: 'absolute',
    top: -2,
    right: -2,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: ADMIN_COLORS.SUCCESS,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkedInBadgeText: {
    fontSize: 12,
    fontWeight: '700',
    color: 'white',
  },
  attendeeListItemContent: {
    flex: 1,
  },
  attendeeListItemName: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 4,
  },
  attendeeListItemEmail: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginBottom: 8,
  },
  attendeeListItemFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  unitBadge: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  unitBadgeText: {
    fontSize: 12,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  statusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  rsvpBadge: {
    backgroundColor: ADMIN_COLORS.SUCCESS + '20',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  rsvpBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: ADMIN_COLORS.SUCCESS,
    textTransform: 'uppercase',
  },
  checkInStatus: {
    alignItems: 'center',
  },
  checkedInStatus: {
    alignItems: 'center',
  },
  checkedInDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: ADMIN_COLORS.SUCCESS,
    marginBottom: 4,
  },
  checkedInText: {
    fontSize: 10,
    fontWeight: '600',
    color: ADMIN_COLORS.SUCCESS,
    textTransform: 'uppercase',
  },
  notCheckedInStatus: {
    alignItems: 'center',
  },
  notCheckedInDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: ADMIN_COLORS.TEXT_TERTIARY,
    marginBottom: 4,
  },
  notCheckedInText: {
    fontSize: 10,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_TERTIARY,
    textTransform: 'uppercase',
  },
});
